/**
 * Currency utility functions to handle floating-point precision issues
 */

/**
 * Safely parse a currency amount from string input
 * Rounds to 2 decimal places to avoid floating-point precision issues
 */
export function parseCurrencyAmount(value: string | number): number {
  if (typeof value === 'number') {
    return Math.round(value * 100) / 100;
  }
  
  if (value === '' || value === null || value === undefined) {
    return 0;
  }
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return 0;
  }
  
  // Round to 2 decimal places to avoid floating-point precision issues
  return Math.round(numValue * 100) / 100;
}

/**
 * Format currency amount for display
 */
export function formatCurrencyAmount(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format currency amount for input fields (without currency symbol)
 */
export function formatCurrencyInput(amount: number): string {
  if (amount === 0) {
    return '';
  }
  
  // Format to 2 decimal places, but remove trailing zeros
  const formatted = amount.toFixed(2);
  return formatted.replace(/\.?0+$/, '');
}

/**
 * Validate currency amount
 */
export function isValidCurrencyAmount(amount: number): boolean {
  return typeof amount === 'number' && 
         !isNaN(amount) && 
         isFinite(amount) && 
         amount >= 0 && 
         amount <= 1000000; // Max $1M
}

/**
 * Convert cents to dollars (for APIs that work with cents)
 */
export function centsToDollars(cents: number): number {
  return Math.round(cents) / 100;
}

/**
 * Convert dollars to cents (for APIs that work with cents)
 */
export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100);
}